import { useState } from 'react'
import { formatCents } from '@core/money'
import { formatIban } from '@core/validators/iban'
import { getPreferredBank } from '@core/banks'
import { remainingCents, type Payable } from '@core/payments'
import { Card, ErrorNote } from '@design/components'
import { PaymentQr } from './PaymentQr'
import { CopySteps } from './CopySteps'
import { DocumentViewer } from '@features/documents/DocumentViewer'
import { Tags } from '@design/Tags'
import { PaymentActions } from './PaymentActions'
import { HistorySheet } from './HistorySheet'
import { Overlay } from '@design/Overlay'

/**
 * The payment screen — the moment money actually leaves.
 *
 * Layout is deliberate (docs/qr-belgium.md §4): amount and IBAN large and
 * ABOVE the code, with the reassurance line between them. Scanning a QR
 * removes the step where copying an account number made you read it, so the
 * numbers have to be impossible to miss on the way past.
 */
export function PaymentCard({
  payable,
  userId,
  onClose,
  onPaid,
}: {
  payable: Payable
  userId: string
  onClose: () => void
  onPaid: () => void
}) {
  const bank = getPreferredBank()
  const [error, setError] = useState<string | null>(null)
  const [viewingDoc, setViewingDoc] = useState(false)
  const [viewingProof, setViewingProof] = useState<string | null>(null)
  const [viewingHistory, setViewingHistory] = useState(false)

  const owed = remainingCents(payable)
  const name = payable.supplierName ?? payable.description

  return (
    <Overlay>
      <header className="shrink-0 pad-safe-top px-3 pb-2 flex items-center justify-between">
        <button onClick={onClose} className="py-2 px-1 -ml-1 text-muted">← Terug</button>
        <span className="text-sm text-muted">{bank.name}</span>
      </header>

      <div className="flex-1 scroll-area px-4 pb-4">
        {/* Numbers first, and big. */}
        <div className="text-center mb-4">
          <p className="text-muted">{name}</p>
          <div className="flex justify-center">
            <Tags
              spaceName={payable.spaceName}
              spaceKind={payable.spaceKind}
              category={payable.category}
            />
          </div>
          <p className="text-4xl font-semibold tabular-nums my-1">{formatCents(owed)}</p>
          {payable.paidCents > 0 && (
            <p className="text-sm text-muted">
              al {formatCents(payable.paidCents)} van {formatCents(payable.amountCents)} betaald
            </p>
          )}
          {payable.iban && (
            <p className="text-lg tabular-nums mt-2">{formatIban(payable.iban)}</p>
          )}
          {payable.isHard && (
            <p className="text-sm text-warn mt-1">
              Deze deadline kan niet wachten
            </p>
          )}
        </div>

        {/* The reassurance line sits between the account number and the code,
            where the eye passes on its way to scanning. */}
        {payable.ibanChanged ? (
          <Card className="mb-4 border-danger/40 bg-danger/5">
            <p className="font-medium mb-1">Rekeningnummer is veranderd</p>
            <p className="text-sm">
              Bel de leverancier op een nummer dat je al had — niet het nummer op de factuur.
            </p>
          </Card>
        ) : payable.ibanSightings > 0 ? (
          <p className="text-sm text-ok text-center mb-4">
            ✓ Zelfde rekeningnummer als de vorige {payable.ibanSightings} facturen
          </p>
        ) : null}

        {payable.iban && !payable.ibanChanged && (
          <div className="mb-5">
            <PaymentQr
              beneficiaryName={name}
              iban={payable.iban}
              amountCents={owed}
              remittance={payable.paymentReference}
              verified={payable.verified}
              profile={bank.profile}
            />
          </div>
        )}

        {payable.iban && (
          <details className="mb-4">
            <summary className="text-sm text-muted py-2 cursor-pointer">
              Zelf invullen
            </summary>
            <div className="pt-2">
              <CopySteps
                iban={payable.iban}
                reference={payable.paymentReference}
                amountCents={owed}
              />
            </div>
          </details>
        )}

        {!payable.verified && (
          <Card className="mb-4 border-warn/40 bg-warn/5">
            <p className="text-sm">
              Deze betaling is nog niet nagekeken, dus er is geen betaalcode.
            </p>
          </Card>
        )}

        {payable.sourceDocumentId && (
          <button
            onClick={() => setViewingDoc(true)}
            className="w-full py-3 text-sm text-muted underline underline-offset-2"
          >
            Origineel document bekijken
          </button>
        )}

        <button
          onClick={() => setViewingHistory(true)}
          className="w-full py-3 text-sm text-muted underline underline-offset-2"
        >
          Wat is hiermee gebeurd?
        </button>

        <ErrorNote>{error}</ErrorNote>
      </div>

      <PaymentActions
        payable={payable}
        userId={userId}
        onPaid={onPaid}
        onProof={setViewingProof}
        setError={setError}
      />

      {viewingHistory && (
        <HistorySheet payable={payable} onClose={() => setViewingHistory(false)} />
      )}

      {viewingProof && (
        <DocumentViewer
          documentId={viewingProof}
          title="Bewijs"
          onClose={() => setViewingProof(null)}
        />
      )}

      {viewingDoc && payable.sourceDocumentId && (
        <DocumentViewer
          documentId={payable.sourceDocumentId}
          title={name}
          onClose={() => setViewingDoc(false)}
        />
      )}
    </Overlay>
  )
}
